import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/i18n/LanguageContext";
import {
  PHONE,
  PHONE_TEL,
  INSTAGRAM_URL,
  INSTAGRAM_HANDLE,
  CONTACT_EMAIL,
  LOGO_URL,
  buildWhatsAppUrl,
} from "@/lib/site";
import { Instagram } from "lucide-react";

const Footer = () => {
  const { t, lang } = useLang();
  const waUrl = buildWhatsAppUrl("default", lang);
  return (
    <footer data-testid="site-footer" className="relative bg-ink text-cream overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 pt-24 pb-10">
        {/* CTA banner */}
        <div className="border-y border-cream/15 py-12 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <p className="overline text-cream/60">{t.footer.tag}</p>
            <h3 className="font-serif text-4xl sm:text-5xl font-light mt-3 leading-tight">
              {t.contact.title}
            </h3>
          </div>
          <div className="flex flex-wrap gap-4 md:justify-end">
            <a
              href={waUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-whatsapp-btn"
              className="px-6 py-3 bg-cream text-ink text-xs tracking-[0.22em] uppercase btn-sweep border border-cream hover:text-cream"
            >
              {t.contact.whatsapp}
            </a>
            <a
              href={PHONE_TEL}
              data-testid="footer-call-btn"
              className="px-6 py-3 border border-cream text-cream text-xs tracking-[0.22em] uppercase btn-sweep sweep-cream hover:text-ink"
            >
              {t.contact.call}
            </a>
          </div>
        </div>

        {/* Cols */}
        <div className="grid md:grid-cols-12 gap-10 py-16">
          <div className="md:col-span-5">
            <img src={LOGO_URL} alt="Iñaki Mayo Dental" className="h-12 w-auto" />
            <p className="mt-6 text-cream/70 max-w-md font-light leading-relaxed">{t.footer.made}</p>
            <div className="mt-8">
              <p className="overline text-cream/50 mb-3">{t.footer.follow}</p>
              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="social-instagram"
                className="inline-flex items-center gap-3 group"
                aria-label="Instagram"
              >
                <span className="w-10 h-10 border border-cream/30 flex items-center justify-center group-hover:bg-cream group-hover:text-ink transition-colors">
                  <Instagram size={16} strokeWidth={1.5} />
                </span>
                <span className="font-serif italic text-lg group-hover:underline">{INSTAGRAM_HANDLE}</span>
              </a>
            </div>
          </div>

          <div className="md:col-span-3">
            <p className="overline text-cream/50 mb-4">{t.footer.sections.explore}</p>
            <ul className="space-y-3 font-light text-cream/85">
              <li><Link data-testid="footer-link-home" to="/" className="link-underline">{t.nav.home}</Link></li>
              <li><Link data-testid="footer-link-treatments" to="/tratamientos" className="link-underline">{t.nav.treatments}</Link></li>
              <li><Link data-testid="footer-link-promotions" to="/promociones" className="link-underline">{t.nav.promotions}</Link></li>
              <li><Link data-testid="footer-link-blog" to="/blog" className="link-underline">{t.nav.blog}</Link></li>
              <li><Link data-testid="footer-link-jobs" to="/trabaja-con-nosotros" className="link-underline">{t.nav.jobs}</Link></li>
              <li><Link data-testid="footer-link-contact" to="/contacto" className="link-underline">{t.nav.contact}</Link></li>
            </ul>
          </div>

          <div className="md:col-span-4">
            <p className="overline text-cream/50 mb-4">{t.footer.sections.contact}</p>
            <ul className="space-y-3 font-light text-cream/85">
              <li>Av. Catalunya, 24</li>
              <li>08290 Cerdanyola del Vallès · Barcelona</li>
              <li className="pt-2"><a href={PHONE_TEL} className="link-underline">{PHONE}</a></li>
              <li><a href={`mailto:${CONTACT_EMAIL}`} className="link-underline">{CONTACT_EMAIL}</a></li>
            </ul>
          </div>
        </div>

        {/* Watermark */}
        <div className="relative overflow-hidden border-t border-cream/15 pt-10">
          <div className="font-serif text-[18vw] leading-none tracking-tighter text-cream/5 select-none whitespace-nowrap pointer-events-none">
            IÑAKI MAYO
          </div>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 pt-8 text-xs text-cream/60">
            <p>{t.footer.rights}</p>
            <div className="flex flex-wrap gap-6">
              <a href="#" className="link-underline">{t.footer.legal}</a>
              <a href="#" className="link-underline">{t.footer.privacy}</a>
              <a href="#" className="link-underline">{t.footer.cookies}</a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
